import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { Box, Button, Grid, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { CameraAlt } from '@material-ui/icons';
import { ExportTypes } from 'modules/reports/interfaces/export';
import { RouteBackground } from 'components';
import { ButtonWithCircularProgress, ScrollBox } from 'common/components';
import { ReactComponent as BackArrowIcon } from 'img/arrow-circle-left.svg';
import { ReactComponent as PrinterIcon } from 'img/icons/printer.svg';
import { ReactComponent as RefundIcon } from 'img/icons/refund.svg';
import { ExportReport } from '../ExportReport/ExportReport';
import commonMessages from 'common/messages/messages';
import { IAttachment } from 'common/interfaces/uploadFile';

const useStyles = makeStyles((theme: Theme) => ({
  reportsContainer: {
    width: '100%',
    height: '100%',
    margin: 0,
  },
  header: {
    width: 'auto',
    margin: '0 0 16px 0',
    padding: '0 !important',
    alignItems: 'center',
  },
  headerText: {
    padding: '0 !important',
  },
  headerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 !important',
  },
  body: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    padding: '0 !important',
  },
  contentWrapper: {
    flex: 1,
    height: '100%',
    minWidth: 0,
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
    },
    overflow: 'auto',
  },
  title: {
    '& p': {
      fontSize: '1.375rem',
    },
  },
}));

interface IReportsContainer {
  className?: string;
  containerClassName?: string;
  title?: string | JSX.Element;
  showMakeSnapshotButton?: boolean;
  showViewSnapshotButton?: boolean;
  showReprintButton?: boolean;
  titleComponent?: JSX.Element;
  backRedirectLink?: string;
  openSnapshotsModal?: () => void;
  hideExportButton?: boolean;
  isPrinting?: boolean;
  children: JSX.Element;
  exportResult?: IAttachment;
  exportTypes?: Array<ExportTypes>;
  exportAction?: (data: Record<string, unknown>) => void;
  onReprint?: () => void;
  showExportTypes?: ExportTypes[];
  showRefundBtn?: boolean;
  openRefund?: () => void;
}

const ReportsContainer: FC<IReportsContainer> = ({
  className,
  containerClassName,
  children,
  title = '',
  showMakeSnapshotButton,
  showViewSnapshotButton,
  showReprintButton = false,
  isPrinting = false,
  titleComponent,
  openSnapshotsModal,
  hideExportButton,
  backRedirectLink,
  exportResult,
  exportTypes = [],
  exportAction,
  onReprint,
  showExportTypes = [ExportTypes.Pdf, ExportTypes.Csv, ExportTypes.EmailPdf, ExportTypes.EmailCsv],
  showRefundBtn,
  openRefund,
}) => {
  const classes = useStyles();

  return (
    <RouteBackground hasMobilePadding className={containerClassName}>
      <Grid
        container
        spacing={2}
        direction="column"
        className={`${classes.reportsContainer} ${className}`}
      >
        <Grid
          item
          container
          className={classNames(classes.header, 'reports-container-header')}
          spacing={1}
          justifyContent="space-between"
        >
          <Grid item className={classes.headerText}>
            <Box display="flex" width="100%" alignItems="center">
              {backRedirectLink && (
                <Box mr={1}>
                  <Link to={backRedirectLink}>
                    <IconButton style={{ padding: 0 }}>
                      <BackArrowIcon style={{ width: 17, height: 17 }} />
                    </IconButton>
                  </Link>
                </Box>
              )}

              <Grid item className={classes.title}>
                <Typography component="p">{title}</Typography>
              </Grid>

              {titleComponent && titleComponent}
            </Box>
          </Grid>

          <Grid item className={classes.headerActions}>
            {showMakeSnapshotButton ? (
              <Button variant="contained" color="primary">
                <FormattedMessage {...commonMessages.makeSnapshotsButton} />
              </Button>
            ) : null}

            {showViewSnapshotButton ? (
              <Button variant="contained" startIcon={<CameraAlt />} onClick={openSnapshotsModal}>
                <FormattedMessage {...commonMessages.snapshotsButton} />
              </Button>
            ) : null}

            {showRefundBtn ? (
              <Button
                variant="contained"
                startIcon={<RefundIcon width={20} height={20} />}
                onClick={openRefund}
              >
                <FormattedMessage {...commonMessages.refundBtn} />
              </Button>
            ) : null}

            {showReprintButton ? (
              <ButtonWithCircularProgress
                isSubmitting={isPrinting}
                variant="contained"
                disableBackground
                startIcon={<PrinterIcon width={20} height={20} />}
                onClick={onReprint}
              >
                <FormattedMessage {...commonMessages.reprintBtn} />
              </ButtonWithCircularProgress>
            ) : null}

            {!hideExportButton ? (
              <ExportReport
                exportAction={exportAction}
                exportResult={exportResult}
                exportTypes={exportTypes}
                showExportTypes={showExportTypes}
              />
            ) : null}
          </Grid>
        </Grid>

        <Grid item className={classNames(classes.body, 'reports-container-body')}>
          <ScrollBox>
            <Box display="flex" style={{ width: '100%', height: '100%' }}>
              <Box className={`${classes.contentWrapper} reportsContentWrapper`}>{children}</Box>
            </Box>
          </ScrollBox>
        </Grid>
      </Grid>
    </RouteBackground>
  );
};

export default ReportsContainer;
